import type { ReactNode } from 'react';
import { Icon, type IconName } from '@/components/Icon';
import './Chip.css';

/**
 * The chip idiom.
 *
 * One component, used by My Towns for followed towns and by Signal Feed for signals — the
 * same layout system at two densities is the point, so these must not fork into two chip
 * components with drifting styles.
 *
 * Selected is a solid --ink fill at weight 800; unselected is white with a divider border at
 * weight 400. Chips scroll horizontally rather than wrapping, an explicit IA decision.
 */

interface ChipProps {
  icon?: IconName;
  label: ReactNode;
  /** Unread count. Rendered only when non-zero — a "0" badge is noise. */
  count?: number;
  selected?: boolean;
  onClick?: () => void;
  /** Opens the chip's own detail (e.g. a signal's full match history) rather than filtering. */
  onCountClick?: () => void;
  countLabel?: string;
}

export function Chip({
  icon,
  label,
  count,
  selected = false,
  onClick,
  onCountClick,
  countLabel,
}: ChipProps) {
  return (
    <button
      type="button"
      className="chip"
      data-selected={selected}
      aria-pressed={selected}
      onClick={onClick}
    >
      {icon && <Icon name={icon} className="chip__icon" />}
      <span className="chip__label">{label}</span>
      {count !== undefined && count > 0 &&
        (onCountClick ? (
          <span
            role="button"
            tabIndex={0}
            className="chip__count chip__count--action"
            aria-label={countLabel}
            onClick={(e) => {
              e.stopPropagation();
              onCountClick();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onCountClick();
              }
            }}
          >
            {count}
          </span>
        ) : (
          <span className="chip__count">{count}</span>
        ))}
    </button>
  );
}

/** The dashed "+ Follow" chip that closes the row and opens the Add Town flow. */
export function AddChip({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="chip chip--add" onClick={onClick}>
      <Icon name="plus" className="chip__icon" />
      <span className="chip__label">{label}</span>
    </button>
  );
}
